// Flat-to-tree sidebar renderer. Groups paths by `/` into collapsible folders.
// Clicking a leaf dispatches an OPEN_EVENT on the container so main.ts can
// open the file via the editor.

import type { FileEntry, FolderSource } from './fs';

// Shared with main.ts's listener — a bare string literal on each side let the
// two drift apart once already (dispatcher said 'sassy:open', listener
// 'kolistat:open', and tree clicks silently died).
export const OPEN_EVENT = 'kolilang:open';

interface TreeNode {
  name: string;
  full: string; // only set on leaves
  children: Map<string, TreeNode>;
}

function buildTree(entries: FileEntry[]): TreeNode {
  const root: TreeNode = { name: '', full: '', children: new Map() };
  for (const e of entries) {
    const parts = e.path.split('/');
    let cur = root;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      let next = cur.children.get(part);
      if (!next) {
        next = { name: part, full: '', children: new Map() };
        cur.children.set(part, next);
      }
      cur = next;
    }
    cur.full = e.path;
  }
  return root;
}

export function renderFileTree(
  container: HTMLElement,
  source: FolderSource,
  entries: FileEntry[],
  activePath: string | null = null,
): void {
  container.innerHTML = '';
  const header = document.createElement('div');
  header.className = 'tree-header';
  header.textContent = `${source.label} · ${entries.length} files`;
  container.appendChild(header);

  const tree = buildTree(entries);
  const ul = document.createElement('ul');
  ul.className = 'tree-root';
  renderNode(container, tree, ul, 0, activePath);
  container.appendChild(ul);
}

function renderNode(
  root: HTMLElement,
  node: TreeNode,
  parent: HTMLElement,
  depth: number,
  activePath: string | null,
): void {
  // children sorted: folders first (have sub-children), then files.
  const entries = [...node.children.values()].sort((a, b) => {
    const aIsLeaf = a.children.size === 0 && a.full.length > 0;
    const bIsLeaf = b.children.size === 0 && b.full.length > 0;
    if (aIsLeaf !== bIsLeaf) return aIsLeaf ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  for (const child of entries) {
    const isLeaf = child.children.size === 0 && child.full.length > 0;
    const li = document.createElement('li');
    li.className = isLeaf ? 'tree-file' : 'tree-dir';
    li.style.paddingLeft = `${depth * 12 + 8}px`;

    const chev = document.createElement('span');
    chev.className = 'chev';
    chev.textContent = isLeaf ? ' ' : '⌄';
    li.appendChild(chev);

    const label = document.createElement('span');
    label.className = 'tree-label';
    label.textContent = child.name;
    li.appendChild(label);

    if (isLeaf) {
      const leafPath = child.full;
      if (leafPath === activePath) li.classList.add('active');
      li.addEventListener('click', () => {
        root.dispatchEvent(
          new CustomEvent(OPEN_EVENT, { detail: { path: leafPath }, bubbles: true }),
        );
      });
      parent.appendChild(li);
    } else {
      const subUl = document.createElement('ul');
      subUl.className = 'tree-subtree';
      subUl.style.display = 'block';
      li.addEventListener('click', (ev) => {
        const shown = subUl.style.display !== 'none';
        subUl.style.display = shown ? 'none' : 'block';
        chev.textContent = shown ? '❯' : '⌄';
        ev.stopPropagation();
      });
      parent.appendChild(li);
      parent.appendChild(subUl);
      renderNode(root, child, subUl, depth + 1, activePath);
    }
  }
}
